// ─────────────────────────────────────────────────────────────────────────────────────────────
// COPIE VERSIONNÉE ET ADAPTÉE POUR TRAME.
// Origine   : dépôt VLT Devis, public/outils/metiers/cvc/froid.mjs
//             (C:\Users\wecha\vlt-devis-saas\public\outils\metiers\cvc\froid.mjs)
// Version   : commit 53f27dce35157919e1bdb92bf9dcd6c1e135e83c du 18/09/2026, fichier du 18/09/2026 17:59
// Empreinte : SHA-256 du source 88F431186EB2F64E54EFE1AE9669DD344EC4BC4B173DDAEA112C1146138617DD
// Copie     : 07/10/2026. La méthode et tous les coefficients d'origine sont INCHANGÉS ; une pièce saisie comme à
//             l'origine donne exactement le même résultat (tests 4 à 10 et 20 de l'origine, mêmes valeurs).
// ADAPTATIONS TRAME (recette du 06/10/2026, § 9.4 — À VALIDER PAR LE DIRIGEANT) :
//   A1. Types de locaux d'ERP ajoutés : salle de culte / salle polyvalente, salle de classe, salle de réunion
//       (bureau et commerce existaient). Ils portent une OCCUPATION « assis » dont les apports par occupant sont des
//       RÉGLAGES modifiables (occupant_sensible_assis_w / occupant_latent_assis_w), affichés avec leur source.
//   A2. Option « air neuf prétraité par une CTA » : le débit de ventilation de la pièce arrive à l'état de soufflage
//       saisi (température, humidité) au lieu de l'état extérieur ; la charge de cet air jusqu'à l'état de soufflage
//       est sortie à part (« à reprendre par la CTA »). Sans état de soufflage saisi : donnée manquante, aucun calcul.
//   A3. Au-delà de 4 m de hauteur : l'alerte F04 est MAINTENUE telle quelle ; aucune stratification n'est calculée.
//   (Les bornes de saisie relevées pour l'ERP sont dans projet.ts, qui remplace etat.mjs.)
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Assistant Climatisation CVC — bilan FROID d'été, pièce par pièce. Moteur PUR.
//
// Reprise de la méthode 1.0.0 (« Facture Eco CVC », METHOD.md : bilan stationnaire d'été +
// psychrométrie ASHRAE) avec les DEUX corrections du cahier des charges (docs/mes-outils/
// cahiers/CDC-CVC.md §3.1) :
//   1. la position de la pièce pilote les termes toiture / plafond sous combles (le champ
//      « dernier étage » de la 1.0.0 était partiellement ignoré) ;
//   2. plusieurs vitrages et orientations par pièce, avec NON-SIMULTANÉITÉ solaire : on retient
//      le créneau horaire le plus chargé (matin / midi / après-midi) au lieu d'additionner des
//      pointes qui ne se produisent pas en même temps.
//
// Ce n'est ni un calcul réglementaire, ni une simulation dynamique : régime stationnaire, pas
// d'inertie, pas de profil horaire, pas de masque. Toutes les valeurs par défaut sont des
// HYPOTHÈSES LOGICIEL affichées et modifiables — aucune n'est cachée dans le code.
// Aucune donnée constructeur, aucun prix, aucune donnée d'entreprise ici.

import { etatAir, P_ATM, type ModeHygro } from './psychro.ts';

/* ------------------------------------------ types ------------------------------------------ */

export interface Vitrage {
  id?: string; nom?: string; orientation?: string;
  largeur_m?: number | null; hauteur_m?: number | null; nombre?: number | null;
  vitrage?: string; protection?: string; g?: number | null; protection_f?: number | null; irradiance_w_m2?: number | null;
}

export interface Piece {
  id: string; nom: string; type?: string;
  surface_m2?: number | null; longueur_m?: number | null; largeur_m?: number | null; hauteur_m?: number | null;
  count?: number | null; position?: string; plancher_bas?: string; facades?: number | null; facade_brute_m2?: number | null;
  isolation?: string; vitrage_defaut?: string; vitrages?: Vitrage[];
  portes_m2?: number | null; porte_u?: number | null;
  u_mur?: number | null; u_toiture?: number | null; u_plancher?: number | null; u_vitrage?: number | null;
  occupants?: number | null; occupant_sensible_w?: number | null; occupant_latent_w?: number | null;
  eclairage_w?: number | null; postes_info?: number | null; poste_info_w?: number | null; tv?: number | null; tv_w?: number | null;
  equipements_w?: number | null; equipements_latent_w?: number | null;
  ventilation_m3h?: number | null; infiltration_volh?: number | null; recuperation_pct?: number | null;
  /** A2 — air neuf de ventilation prétraité par une CTA, soufflé à l'état ci-dessous. */
  air_neuf_pretraite?: boolean; soufflage_t?: number | null; soufflage_hr?: number | null;
  theta_int_c?: number | null; intermittence?: boolean; zone_id?: string | null;
  mur_local_non_chauffe_m2?: number | null; pont_thermique_w_m2k?: number | null;
}

export interface Conditions {
  ext_t: number; ext_valeur: number; ext_mode: ModeHygro;
  int_t: number; int_valeur: number; int_mode: ModeHygro;
  pression: number; source?: string; verifiee?: boolean;
}

export interface AlerteCvc { code: string; niveau: 'bloquant' | 'attention'; message: string; cible: string | null }

export type Rang = [string, number];

export interface ResultatFroid {
  id: string; nom: string; count: number; manques: string[]; erreur?: boolean;
  hypotheses: string[]; alertes: AlerteCvc[];
  surface_m2: number; hauteur_m: number | null;
  rangs_sensibles: Rang[]; rangs_latents: Rang[];
  sensible_w: number; latent_w: number; total_w: number; sensible_hors_solaire_w: number;
  solaire_creneaux_w: number[]; creneau: string; creneau_nom: string;
  dimensionnement_w: number; shr: number | null; ratio_w_m2: number; debit_air_m3h: number;
  air_sensible_w: number; air_latent_w: number;
  /** A2 — charge de l'air neuf jusqu'à l'état de soufflage, à reprendre par la CTA (null sans prétraitement). */
  air_neuf_cta: { sensible_w: number; latent_w: number; total_w: number; debit_m3h: number } | null;
  apports_occupant: { sensible_w: number; latent_w: number; source: string };
  coefficients: Record<string, number>;
  exterieur: Record<string, number | null>; interieur: Record<string, number | null>;
}

/** Résultat incomplet : une donnée manque, ou le calcul est refusé (jamais un zéro muet). */
export interface ResultatFroidIncomplet {
  id: string; nom: string; count: number; manques: string[]; erreur?: boolean; hypotheses: string[]; alertes: AlerteCvc[];
}

/* --------------------------- hypothèses logiciel (modifiables) --------------------------- */

// U (W/m².K) murs / toiture-plafond / plancher bas. Préréglages 1.0.0, repris par le CDC §3.2.
export const U_ISOLATION: Readonly<Record<string, readonly [number, number, number]>> = Object.freeze({
  tres_faible: [2, 1.8, 1.5],
  moyenne: [1.2, 0.8, 0.8],
  bonne: [0.45, 0.3, 0.4],
  tres_bonne: [0.2, 0.15, 0.2],
  inconnue: [1.5, 1, 0.9],
});

export const ISOLATIONS = Object.freeze([
  { id: 'tres_faible', nom: 'Très faible (avant 1975, non isolé)' },
  { id: 'moyenne', nom: 'Moyenne (1975-2000)' },
  { id: 'bonne', nom: 'Bonne (2001-2012)' },
  { id: 'tres_bonne', nom: 'Très bonne (après 2012)' },
  { id: 'inconnue', nom: 'Ne sait pas (hypothèse prudente)' },
]);

export const U_VITRAGE: Readonly<Record<string, number>> = Object.freeze({ simple: 5.7, double: 2.8, triple: 1.1 });
export const G_VITRAGE: Readonly<Record<string, number>> = Object.freeze({ simple: 0.85, double: 0.65, triple: 0.5 });
export const VITRAGES = Object.freeze([
  { id: 'simple', nom: 'Simple vitrage' },
  { id: 'double', nom: 'Double vitrage' },
  { id: 'triple', nom: 'Triple vitrage' },
]);

export const PROTECTIONS = Object.freeze([
  { id: 'aucune', nom: 'Aucune', f: 1 },
  { id: 'rideau', nom: 'Rideau intérieur', f: 0.8 },
  { id: 'store_ext', nom: 'Store extérieur', f: 0.4 },
  { id: 'volet', nom: 'Volet extérieur fermé', f: 0.25 },
]);

export const CRENEAUX = Object.freeze([
  { id: 'matin', nom: 'Matin' },
  { id: 'midi', nom: 'Midi' },
  { id: 'apres_midi', nom: 'Après-midi' },
]);

// Éclairement de pointe I (W/m² de vitrage) par orientation et créneau — CDC §3.1, correction 2.
// Hypothèse logiciel ALIGNÉE sur les pointes de la méthode 1.0.0 : une pièce à orientation unique
// donne exactement le même résultat qu'avant. Ce n'est pas une base solaire météorologique.
export const IRRADIANCE: Readonly<Record<string, readonly [number, number, number]>> = Object.freeze({
  Nord: [150, 150, 150],
  'Nord-est': [350, 150, 150],
  Est: [500, 150, 150],
  'Sud-est': [500, 400, 150],
  Sud: [300, 550, 300],
  'Sud-ouest': [150, 400, 600],
  Ouest: [150, 150, 650],
  'Nord-ouest': [150, 150, 350],
  Toit: [500, 800, 500],
});
export const ORIENTATIONS = Object.freeze(Object.keys(IRRADIANCE));

export const POSITIONS = Object.freeze([
  { id: 'intermediaire', nom: 'Étage intermédiaire' },
  { id: 'rdc', nom: 'Rez-de-chaussée' },
  { id: 'sous_toiture', nom: 'Dernier niveau sous toiture' },
  { id: 'sous_combles', nom: 'Dernier niveau sous combles perdus' },
]);

/**
 * Types de pièce. `occupation` choisit les apports par occupant par défaut (réglages) :
 *  - 'courante' (défaut d'origine) : occupant_sensible_w / occupant_latent_w ;
 *  - 'assis' (A1, locaux d'ERP où le public est assis) : occupant_sensible_assis_w / occupant_latent_assis_w ;
 *  - atelier (origine) : occupant_sensible_atelier_w / occupant_latent_atelier_w.
 * `erp` : type ajouté pour TRAME (A1).
 */
export interface TypePiece { id: string; nom: string; theta: number; atelier: boolean; occupation?: 'courante' | 'assis'; erp?: boolean }
export const TYPES_PIECE: readonly TypePiece[] = Object.freeze([
  { id: 'sejour', nom: 'Séjour, salon', theta: 20, atelier: false },
  { id: 'chambre', nom: 'Chambre', theta: 20, atelier: false },
  { id: 'bureau', nom: 'Bureau', theta: 20, atelier: false },
  { id: 'salle_de_bains', nom: 'Salle de bains', theta: 22, atelier: false },
  { id: 'circulation', nom: 'Circulation, cellier', theta: 16, atelier: false },
  { id: 'cuisine', nom: 'Cuisine', theta: 20, atelier: false },
  { id: 'restaurant', nom: 'Salle de restaurant', theta: 20, atelier: false },
  { id: 'atelier', nom: 'Atelier, local technique', theta: 20, atelier: true },
  { id: 'commerce', nom: 'Commerce, accueil', theta: 20, atelier: false },
  // A1 — locaux d'ERP (température de confort 20 °C : même hypothèse logiciel que les autres pièces de vie).
  { id: 'salle_culte', nom: 'Salle de culte, salle polyvalente', theta: 20, atelier: false, occupation: 'assis', erp: true },
  { id: 'classe', nom: 'Salle de classe', theta: 20, atelier: false, occupation: 'assis', erp: true },
  { id: 'salle_reunion', nom: 'Salle de réunion', theta: 20, atelier: false, occupation: 'assis', erp: true },
]);

// Hypothèses logiciel du bilan d'été. Toutes reprises telles quelles dans l'écran « Hypothèses ».
export const REGLAGES_FROID = Object.freeze({
  dt_solaire_mur_k: 5,        // échauffement solaire équivalent des murs opaques
  dt_solaire_toit_k: 15,      // idem toiture exposée
  dt_comble_k: 10,            // température de comble perdu = T extérieure + 10 K
  occupant_sensible_w: 75,
  occupant_latent_w: 55,
  occupant_sensible_atelier_w: 150,
  occupant_latent_atelier_w: 100,
  // A1 — public assis (culte, classe, réunion). À VALIDER : voir SOURCES_APPORTS_OCCUPANT.
  occupant_sensible_assis_w: 70,
  occupant_latent_assis_w: 45,
  poste_info_w: 150,
  tv_w: 100,
  porte_u: 2,
  securite_pct: 10,   // coefficient de sécurité sur le besoin (ce n'est PAS une marge commerciale)
});
export type ReglagesFroid = { -readonly [K in keyof typeof REGLAGES_FROID]: number };

/**
 * Origine affichée des apports par occupant (A1). Ce sont des HYPOTHÈSES LOGICIEL : les valeurs courantes sont celles de
 * l'outil d'origine (« estimation selon activité ») ; celles du public assis sont l'ordre de grandeur « assis, activité
 * très légère » des tables d'apports humains de l'ASHRAE (Handbook Fundamentals, chapitre 18, tableau 1, valeurs
 * ajustées), cohérent avec les 100 à 120 W par personne assise retenus par l'analyse experte du dossier de recette.
 * À vérifier sur l'édition détenue et à valider par le dirigeant avant usage contractuel.
 */
export const SOURCES_APPORTS_OCCUPANT = Object.freeze({
  courante: 'Hypothèse logiciel de l’outil d’origine (activité de bureau modérée), à valider.',
  assis: 'Hypothèse logiciel : public assis, activité très légère — ordre de grandeur des tables d’apports humains ASHRAE (Fundamentals, ch. 18, tab. 1) ; à valider.',
  atelier: 'Hypothèse logiciel de l’outil d’origine (travail actif), à valider.',
  saisie: 'Valeur saisie pour cette pièce.',
});

export const CONDITIONS_DEFAUT: Readonly<Conditions> = Object.freeze({
  ext_t: 35, ext_valeur: 40, ext_mode: 'hr',
  int_t: 26, int_valeur: 50, int_mode: 'hr',
  pression: P_ATM,
  source: 'Conditions extérieures 35 °C / 40 % HR et intérieures 26 °C / 50 % HR : hypothèses logiciel, à confirmer localement.',
  verifiee: false,
});

/* ------------------------------------- utilitaires ------------------------------------- */

const n = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const somme = (rangs: Rang[]) => rangs.reduce((s, r) => s + r[1], 0);

/** Géométrie : surface, périmètre, surface de façade brute. Les estimations sont tracées. */
export function geometrie(p: Piece, hypotheses: string[]) {
  let surface = n(p.surface_m2);
  const L = n(p.longueur_m);
  const l = n(p.largeur_m);
  if (L != null && l != null && L > 0 && l > 0) {
    const s = L * l;
    if (surface == null || Math.abs(s - surface) > 0.1) hypotheses.push('Surface issue de longueur × largeur.');
    surface = s;
  }
  if (surface == null || surface <= 0) return null;
  const longueur = L != null && L > 0 ? L : Math.sqrt(surface);
  const largeur = l != null && l > 0 ? l : surface / longueur;
  if (L == null || l == null) hypotheses.push('Géométrie rectangulaire estimée à partir de la surface (pas de relevé sur place).');
  const facades = n(p.facades);
  const hauteur = n(p.hauteur_m) as number;
  const brute = (longueur + largeur) * 2 * hauteur * (facades == null ? 2 : facades) / 4;
  if (facades == null) hypotheses.push('Nombre de façades extérieures non renseigné : 2 façades supposées.');
  return { surface, longueur, largeur, facade_brute_m2: brute };
}

/** Surface totale des vitrages d'une pièce (m²). */
export const surfaceVitrages = (vitrages: Vitrage[] | undefined) => (vitrages || []).reduce((s, v) => {
  const a = n(v.largeur_m);
  const b = n(v.hauteur_m);
  const q = n(v.nombre);
  return s + (a == null || b == null ? 0 : a * b * (q == null ? 1 : q));
}, 0);

/**
 * Apport solaire par les vitrages, par créneau horaire (W).
 * Q(c) = Somme_i S_i x I(o_i, c) x g_i x F_p,i ; on retient ensuite le créneau le plus chargé.
 */
export function solaireParCreneau(vitrages: Vitrage[] | undefined, hypotheses: string[] = []) {
  const q = [0, 0, 0];
  for (const v of vitrages || []) {
    const a = n(v.largeur_m);
    const b = n(v.hauteur_m);
    if (a == null || b == null) continue;
    const s = a * b * (n(v.nombre) == null ? 1 : n(v.nombre)!);
    const g = n(v.g) != null ? n(v.g)! : (G_VITRAGE[v.vitrage ?? ''] == null ? G_VITRAGE.double : G_VITRAGE[v.vitrage!]);
    const prot = PROTECTIONS.find((x) => x.id === v.protection);
    const f = n(v.protection_f) != null ? n(v.protection_f)! : (prot ? prot.f : 1);
    const forcee = n(v.irradiance_w_m2);
    const table = IRRADIANCE[v.orientation ?? ''] || IRRADIANCE.Sud;
    if (forcee != null) hypotheses.push('Éclairement forcé à ' + forcee + ' W/m² pour un vitrage (même valeur aux trois créneaux).');
    for (let c = 0; c < 3; c++) q[c] += s * (forcee != null ? forcee : table[c]) * g * f;
  }
  return q;
}

/** Apports par occupant retenus pour une pièce (A1) : saisie de la pièce, sinon réglage selon le type. */
export function apportsOccupant(piece: Piece, R: ReglagesFroid) {
  const type = TYPES_PIECE.find((t) => t.id === piece.type);
  const atelier = type?.atelier === true;
  const assis = type?.occupation === 'assis';
  const qsDefaut = atelier ? R.occupant_sensible_atelier_w : assis ? R.occupant_sensible_assis_w : R.occupant_sensible_w;
  const qlDefaut = atelier ? R.occupant_latent_atelier_w : assis ? R.occupant_latent_assis_w : R.occupant_latent_w;
  const saisiS = n(piece.occupant_sensible_w);
  const saisiL = n(piece.occupant_latent_w);
  const sourceDefaut = atelier ? SOURCES_APPORTS_OCCUPANT.atelier : assis ? SOURCES_APPORTS_OCCUPANT.assis : SOURCES_APPORTS_OCCUPANT.courante;
  return {
    sensible_w: saisiS != null ? saisiS : qsDefaut,
    latent_w: saisiL != null ? saisiL : qlDefaut,
    source: saisiS != null || saisiL != null ? SOURCES_APPORTS_OCCUPANT.saisie : sourceDefaut,
  };
}

/* ---------------------------------- bilan d'une pièce ---------------------------------- */

/**
 * Charge frigorifique d'UNE pièce (un exemplaire ; le nombre de pièces identiques est appliqué
 * par zones.ts). Retourne { manques } si une donnée indispensable manque : jamais un zéro muet.
 */
export function calculerPiece(piece: Piece, conditions: Conditions, reglages: Partial<ReglagesFroid> = REGLAGES_FROID): ResultatFroid | ResultatFroidIncomplet {
  const R: ReglagesFroid = { ...REGLAGES_FROID, ...reglages };
  const hypotheses: string[] = [];
  const alertes: AlerteCvc[] = [];
  const manques: string[] = [];
  if (n(piece.surface_m2) == null && !(n(piece.longueur_m) && n(piece.largeur_m))) manques.push('surface');
  if (n(piece.hauteur_m) == null) manques.push('hauteur');
  // A2 : un air neuf « prétraité » sans état de soufflage n'est pas calculé (on ne devine pas la sortie de la CTA).
  const pretraite = piece.air_neuf_pretraite === true && (n(piece.ventilation_m3h) || 0) > 0;
  if (pretraite && (n(piece.soufflage_t) == null || n(piece.soufflage_hr) == null)) manques.push('soufflage');
  if (manques.length) return { id: piece.id, nom: piece.nom, count: n(piece.count) || 1, manques, hypotheses, alertes };

  const ext = etatAir(conditions.ext_t, conditions.ext_valeur, conditions.ext_mode, conditions.pression);
  const int = etatAir(conditions.int_t, conditions.int_valeur, conditions.int_mode, conditions.pression);
  const g = geometrie(piece, hypotheses);
  if (!g) return { id: piece.id, nom: piece.nom, count: n(piece.count) || 1, manques: ['surface'], hypotheses, alertes };

  const dt = Math.max(0, conditions.ext_t - conditions.int_t);
  const u = U_ISOLATION[piece.isolation ?? ''] || U_ISOLATION.inconnue;
  if (!U_ISOLATION[piece.isolation ?? ''] || piece.isolation === 'inconnue') {
    hypotheses.push('Isolation inconnue : hypothèse prudente U murs 1,5 / toiture-plafond 1,0 / plancher 0,9 W/m².K.');
    alertes.push({ code: 'F13', niveau: 'attention', message: piece.nom + ' : niveau d’isolation « ne sait pas » — hypothèse prudente retenue, à confirmer sur place.', cible: piece.id });
  }
  const uMur = n(piece.u_mur) != null ? n(piece.u_mur)! : u[0];
  const uToit = n(piece.u_toiture) != null ? n(piece.u_toiture)! : u[1];
  const uVitre = n(piece.u_vitrage) != null ? n(piece.u_vitrage)! : (U_VITRAGE[piece.vitrage_defaut ?? ''] || U_VITRAGE.double);

  const sVitres = surfaceVitrages(piece.vitrages);
  const sPortes = n(piece.portes_m2) || 0;
  const uPorte = n(piece.porte_u) != null ? n(piece.porte_u)! : R.porte_u;
  const brute = n(piece.facade_brute_m2) != null ? n(piece.facade_brute_m2)! : g.facade_brute_m2;
  if (sVitres + sPortes > brute) {
    alertes.push({
      code: 'F03', niveau: 'bloquant', cible: piece.id,
      message: piece.nom + ' : vitrages et portes (' + (sVitres + sPortes).toFixed(2) + ' m²) dépassent la surface de façade (' + brute.toFixed(2) + ' m²). Corrigez les cotes ou le nombre de façades.',
    });
    return { id: piece.id, nom: piece.nom, count: n(piece.count) || 1, manques: [], erreur: true, hypotheses, alertes };
  }
  const sMurs = Math.max(0, brute - sVitres - sPortes);

  // Toiture / plafond selon la position de la pièce (correction n° 1 du CDC).
  const sousToiture = piece.position === 'sous_toiture';
  const sousCombles = piece.position === 'sous_combles';
  const sHaut = sousToiture || sousCombles ? g.surface : 0;
  if (sousToiture) hypotheses.push('Toiture exposée : échauffement solaire équivalent +' + R.dt_solaire_toit_k + ' K.');
  if (sousCombles) hypotheses.push('Comble perdu supposé à la température extérieure +' + R.dt_comble_k + ' K.');
  hypotheses.push('Échauffement solaire équivalent des murs opaques : +' + R.dt_solaire_mur_k + ' K.');

  // Solaire : créneau le plus chargé (correction n° 2 du CDC).
  const creneaux = solaireParCreneau(piece.vitrages, hypotheses);
  let iCreneau = 0;
  for (let c = 1; c < 3; c++) if (creneaux[c] > creneaux[iCreneau]) iCreneau = c;
  const solaire = creneaux[iCreneau];

  // Apports internes (A1 : apports par occupant selon l'occupation du type, ou saisis pour la pièce).
  const occupant = apportsOccupant(piece, R);
  const qs = occupant.sensible_w;
  const ql = occupant.latent_w;
  const occ = n(piece.occupants) || 0;
  if (occ > 0) hypotheses.push('Occupants : ' + qs + ' W sensibles + ' + ql + ' W latents par personne — ' + occupant.source);

  // Air extérieur : ventilation + infiltrations, pris à l'état extérieur. Pas de double comptage.
  const vent = n(piece.ventilation_m3h) || 0;
  const infil = n(piece.infiltration_volh);
  if (infil == null) hypotheses.push('Taux d’infiltration non renseigné : infiltrations non comptées — saisissez un taux en vol/h.');
  const debitInfil = infil == null ? 0 : infil * g.surface * n(piece.hauteur_m)!;
  const debit = vent + debitInfil;
  let airS: number;
  let airL: number;
  let airNeufCta: ResultatFroid['air_neuf_cta'] = null;
  if (!pretraite) {
    // Calcul d'ORIGINE, inchangé.
    const masse = debit / 3600 / ext.v;
    const airSigne = masse * (ext.h - int.h) * 1000;
    airS = masse * (1.006 + 1.86 * ext.w) * (ext.t - int.t) * 1000;
    airL = airSigne - airS;
  } else {
    // A2 : la ventilation arrive à l'état de soufflage de la CTA ; les infiltrations restent à l'état extérieur.
    const souf = etatAir(n(piece.soufflage_t)!, n(piece.soufflage_hr)!, 'hr', conditions.pression);
    // Débit d'air neuf compté comme à l'origine : m³/h d'air EXTÉRIEUR (masse = débit / volume spécifique extérieur).
    const mVent = vent / 3600 / ext.v;
    const mInf = debitInfil / 3600 / ext.v;
    const sVent = mVent * (1.006 + 1.86 * souf.w) * (souf.t - int.t) * 1000;
    const tVent = mVent * (souf.h - int.h) * 1000;
    const sInf = mInf * (1.006 + 1.86 * ext.w) * (ext.t - int.t) * 1000;
    const tInf = mInf * (ext.h - int.h) * 1000;
    airS = sVent + sInf;
    airL = (tVent + tInf) - airS;
    // Charge de l'air neuf de l'extérieur jusqu'à l'état de soufflage : elle revient à la batterie froide de la CTA.
    const ctaS = mVent * (1.006 + 1.86 * ext.w) * (ext.t - souf.t) * 1000;
    const ctaT = mVent * (ext.h - souf.h) * 1000;
    airNeufCta = { sensible_w: Math.max(0, ctaS), latent_w: Math.max(0, ctaT - ctaS), total_w: Math.max(0, ctaT), debit_m3h: vent };
    hypotheses.push('Air neuf prétraité par une CTA : soufflé à ' + String(souf.t).replace('.', ',') + ' °C / ' + String(Math.round(souf.hr)) + ' % HR (état saisi) ; sa charge jusqu’à cet état est à reprendre par la CTA, elle ne compte pas dans le besoin des unités terminales.');
  }
  if (airS < 0 || airL < 0) hypotheses.push('Crédits de refroidissement ou de séchage de l’air neuf non déduits (pré-dimensionnement prudent).');

  const uPoste = n(piece.poste_info_w) != null ? n(piece.poste_info_w)! : R.poste_info_w;
  const uTv = n(piece.tv_w) != null ? n(piece.tv_w)! : R.tv_w;

  const rangsSensibles = ([
    ['Murs extérieurs', uMur * sMurs * (dt + R.dt_solaire_mur_k)],
    ['Vitrages (transmission)', uVitre * sVitres * dt],
    ['Portes', uPorte * sPortes * dt],
    ['Toiture', sousToiture ? uToit * sHaut * (dt + R.dt_solaire_toit_k) : 0],
    ['Plafond sous combles', sousCombles ? uToit * sHaut * (dt + R.dt_comble_k) : 0],
    ['Solaire vitrages (' + CRENEAUX[iCreneau].nom.toLowerCase() + ')', solaire],
    ['Occupants (sensible)', occ * qs],
    ['Éclairage', n(piece.eclairage_w) || 0],
    ['Informatique', (n(piece.postes_info) || 0) * uPoste],
    ['Télévisions', (n(piece.tv) || 0) * uTv],
    ['Équipements', n(piece.equipements_w) || 0],
    [pretraite ? 'Air neuf prétraité et infiltrations (sensible)' : 'Air extérieur (sensible)', Math.max(0, airS)],
  ] as Rang[]).filter((r) => r[1] !== 0);

  const rangsLatents = ([
    ['Occupants (latent)', occ * ql],
    ['Équipements (latent)', n(piece.equipements_latent_w) || 0],
    [pretraite ? 'Air neuf prétraité et infiltrations (latent)' : 'Air extérieur (latent)', Math.max(0, airL)],
  ] as Rang[]).filter((r) => r[1] !== 0);

  const sensible = somme(rangsSensibles);
  const latent = somme(rangsLatents);
  const total = sensible + latent;
  const securite = n(R.securite_pct) == null ? 10 : R.securite_pct;
  const ratio = total / g.surface;

  if (ratio > 250 || ratio < 35) {
    alertes.push({
      code: 'F01', niveau: 'attention', cible: piece.id,
      message: piece.nom + ' : charge de ' + Math.round(ratio) + ' W/m² ' + (ratio > 250 ? '(au-delà de 250)' : '(sous 35)') + ' — vérifiez vitrages, débits et équipements.',
    });
  }
  if (n(piece.hauteur_m)! > 4) {
    // A3 : alerte maintenue telle quelle — aucune stratification n'est calculée.
    alertes.push({ code: 'F04', niveau: 'attention', cible: piece.id, message: piece.nom + ' : hauteur supérieure à 4 m — stratification non prise en compte ; préférez une cassette ou un gainable et vérifiez la reprise.' });
  }
  const dissipe = (n(piece.eclairage_w) || 0) + (n(piece.equipements_w) || 0) + (n(piece.postes_info) || 0) * uPoste + (n(piece.tv) || 0) * uTv;
  if (sensible > 0 && dissipe > 0.5 * sensible) {
    alertes.push({ code: 'F09', niveau: 'attention', cible: piece.id, message: piece.nom + ' : les équipements dépassent la moitié de la charge sensible — vérifiez les puissances réellement dissipées.' });
  }
  if (['cuisine', 'restaurant', 'atelier'].includes(piece.type ?? '')) {
    alertes.push({ code: 'F10', niveau: 'attention', cible: piece.id, message: piece.nom + ' : extraction, apports de cuisson ou de procédé et compensation d’air non traités par l’outil.' });
  }
  if (piece.type === 'salle_de_bains') {
    alertes.push({ code: 'F11', niveau: 'attention', cible: piece.id, message: piece.nom + ' : unité en salle de bains — volumes électriques (NF C 15-100) et humidité à vérifier sur la notice.' });
  }
  hypotheses.push('Régime stationnaire : inertie, profil horaire et stockage thermique non simulés.');
  if (!conditions.verifiee && conditions.source) hypotheses.push(conditions.source);

  return {
    id: piece.id,
    nom: piece.nom,
    count: n(piece.count) || 1,
    manques: [],
    surface_m2: g.surface,
    hauteur_m: n(piece.hauteur_m),
    rangs_sensibles: rangsSensibles,
    rangs_latents: rangsLatents,
    sensible_w: sensible,
    latent_w: latent,
    total_w: total,
    sensible_hors_solaire_w: sensible - solaire,
    solaire_creneaux_w: creneaux,
    creneau: CRENEAUX[iCreneau].id,
    creneau_nom: CRENEAUX[iCreneau].nom,
    dimensionnement_w: total * (1 + securite / 100),
    shr: total ? sensible / total : null,
    ratio_w_m2: ratio,
    debit_air_m3h: debit,
    air_sensible_w: airS,
    air_latent_w: airL,
    air_neuf_cta: airNeufCta,
    apports_occupant: occupant,
    coefficients: {
      u_mur: uMur, u_toiture: uToit, u_vitrage: uVitre, u_porte: uPorte,
      surface_murs_m2: sMurs, surface_vitrages_m2: sVitres, surface_portes_m2: sPortes,
      facade_brute_m2: brute, dt_k: dt,
    },
    exterieur: { t: ext.t, hr: ext.hr, w_g_kg: ext.w * 1000, h_kj_kg: ext.h, humide_c: ext.humide, v_m3_kg: ext.v },
    interieur: { t: int.t, hr: int.hr, w_g_kg: int.w * 1000, h_kj_kg: int.h, humide_c: int.humide },
    hypotheses: [...new Set(hypotheses)],
    alertes,
  };
}

export const estComplet = (r: ResultatFroid | ResultatFroidIncomplet): r is ResultatFroid => !r.erreur && !r.manques.length && 'total_w' in r;
